import { Element } from '../Element';

/**
 * Interface for legacy audio element properties that may exist in JSON data
 */
interface LegacyAudioProps {
  src?: string;
  isMuted?: boolean;
}

/**
 * Interface for audio element state
 */
interface AudioElementState {
  source: string;
  volume: string;
  loop: boolean;
  audio_fade_in: number | null;
  audio_fade_out: number | null;
}

/**
 * AudioElement represents an audio clip on the timeline
 * Aligned with Creatomate properties and structure
 */
export class AudioElement extends Element {
  private _source: string; // Creatomate uses 'source' for media URL
  private _volume: string = '100%'; // Creatomate uses percentage strings
  private _loop: boolean = false; // When true, the audio starts over when it reaches the end
  private _audio_fade_in: number | null = null; // Duration in seconds for audio fade in
  private _audio_fade_out: number | null = null; // Duration in seconds for audio fade out

  constructor(props: Partial<AudioElement & LegacyAudioProps> = {}) {
    super({ ...props, type: 'audio' });

    // Support both source and src properties for backwards compatibility
    this._source = props.source ?? props.src ?? '';

    // Handle volume as a percentage string (Creatomate format)
    if (props.volume !== undefined) {
      if (typeof props.volume === 'number') {
        // Convert number (0-1) to percentage string
        const percentage = Math.round(props.volume * 100);
        this._volume = `${percentage}%`;
      } else if (typeof props.volume === 'string') {
        this._volume = props.volume;
      }
    } else if (props.isMuted) {
      this._volume = '0%';
    }

    // Handle loop property (note: loop cannot be used with trim_start/trim_duration per API docs)
    this._loop = props.loop ?? false;

    // Handle audio fade properties
    this._audio_fade_in = props.audio_fade_in ?? null;
    this._audio_fade_out = props.audio_fade_out ?? null;
  }

  // Getters and setters
  get source(): string {
    return this._source;
  }

  set source(value: string) {
    this._source = value;
  }

  get volume(): string {
    return this._volume;
  }

  set volume(value: string) {
    this._volume = value;
  }

  get loop(): boolean {
    return this._loop;
  }

  set loop(value: boolean) {
    this._loop = value;
  }

  get audio_fade_in(): number | null {
    return this._audio_fade_in;
  }

  set audio_fade_in(value: number | null) {
    this._audio_fade_in = value;
  }

  get audio_fade_out(): number | null {
    return this._audio_fade_out;
  }

  set audio_fade_out(value: number | null) {
    this._audio_fade_out = value;
  }

  /**
   * Captures the mutable state of the audio element.
   * @returns A record of all mutable properties.
   */
  override getState(): Record<string, unknown> & AudioElementState {
    return {
      ...super.getState(),
      source: this._source,
      volume: this._volume,
      loop: this._loop,
      audio_fade_in: this._audio_fade_in,
      audio_fade_out: this._audio_fade_out,
    };
  }

  /**
   * Restores the audio element's mutable state from a state record.
   * @param state The state to restore.
   */
  override restoreState(
    state: Record<string, unknown> & Partial<AudioElementState>,
  ): void {
    super.restoreState(state);
    if (state.source !== undefined) this._source = state.source;
    if (state.volume !== undefined) this._volume = state.volume;
    if (state.loop !== undefined) this._loop = state.loop;
    if (state.audio_fade_in !== undefined)
      this._audio_fade_in = state.audio_fade_in;
    if (state.audio_fade_out !== undefined)
      this._audio_fade_out = state.audio_fade_out;
  }

  /**
   * Updates the audio element properties
   * Override base Element update to handle AudioElement-specific properties
   */
  override update(props: Partial<AudioElement>): void {
    // Call parent update for base properties
    super.update(props);

    // Handle AudioElement-specific properties
    if (props.source !== undefined) this._source = props.source;
    if (props.volume !== undefined) {
      if (typeof props.volume === 'number') {
        // Convert number (0-100) to percentage string
        this._volume = `${props.volume}%`;
      } else if (typeof props.volume === 'string') {
        this._volume = props.volume;
      }
    }
    if (props.loop !== undefined) this._loop = props.loop;
    if (props.audio_fade_in !== undefined)
      this._audio_fade_in = props.audio_fade_in;
    if (props.audio_fade_out !== undefined)
      this._audio_fade_out = props.audio_fade_out;
  }

  /**
   * Returns the effective duration of the audio element
   * This accounts for trim_duration if specified
   */
  getEffectiveDuration(): number | null | string {
    // If explicit duration is set (from Element base class), use that
    if (this.duration !== null && this.duration !== undefined) {
      // Special handling for 'media' duration value per Creatomate API spec
      if (this.duration === 'media') {
        return 'media';
      }
      return this.duration;
    }

    // If trim_duration is specified, that's the effective duration
    if (this.trim_duration !== null) {
      return this.trim_duration;
    }

    // Otherwise return null which means "use the full media duration" (represented as 'media' in Creatomate)
    return null;
  }

  /**
   * Creates a deep clone of this audio element
   * Override the base Element.clone() to ensure we create an AudioElement, not a generic Element
   */
  override clone(): AudioElement {
    return new AudioElement({
      ...this.getState(),
      // Ensure specific audio properties are copied
      source: this._source,
      volume: this._volume,
      trim_start: this.trim_start,
      trim_duration: this.trim_duration,
      loop: this._loop,
      audio_fade_in: this._audio_fade_in,
      audio_fade_out: this._audio_fade_out,
    });
  }

  getMediaStartTime(): number {
    return this.trim_start ?? 0;
  }

  getMediaEndTime(): number {
    const startTime = this.trim_start ?? 0;
    return startTime + this.durationSeconds;
  }

  protected override getAdditionalProperties(): Record<string, unknown> {
    // Only include properties in the JSON if they have non-default values
    const props: Record<string, unknown> = {
      source: this._source,
      volume: this._volume,
    };

    // Only include trim_start if it's not null, and format it as "X s"
    if (this.trim_start !== null) {
      props.trim_start = this.formatTimeProperty(this.trim_start);
    }

    // Only include trim_duration if it's not null, and format it as "X s"
    if (this.trim_duration !== null) {
      props.trim_duration = this.formatTimeProperty(this.trim_duration);
    }

    // Only include loop if it's true (default is false)
    if (this._loop) {
      props.loop = this._loop;
    }

    // Only include fade properties if they're not null, and format as "X s"
    if (this._audio_fade_in !== null) {
      props.audio_fade_in = this.formatTimeProperty(this._audio_fade_in);
    }

    if (this._audio_fade_out !== null) {
      props.audio_fade_out = this.formatTimeProperty(this._audio_fade_out);
    }

    return props;
  }

  /**
   * Creates an AudioElement from JSON data
   */
  static override fromJSON(json: unknown): AudioElement {
    // Validate that json is an object
    if (!json || typeof json !== 'object' || Array.isArray(json)) {
      throw new Error('Invalid JSON: expected object for AudioElement');
    }

    // Type narrow to Record<string, unknown> after validation
    const data = json as Record<string, unknown>;

    // Extract values with fallbacks to support different formats
    const audioSource =
      (typeof data.source === 'string' ? data.source : '') ||
      (typeof data.src === 'string' ? data.src : '') ||
      '';

    let volumeStr: string = '100%';
    const volumeValue = data.volume;
    // Support legacy properties or formats
    if (typeof volumeValue === 'number') {
      // Convert number (0-1) to percentage string
      const percentage = Math.round(volumeValue * 100);
      volumeStr = `${percentage}%`;
    } else if (typeof volumeValue === 'string') {
      volumeStr = volumeValue;
    }

    // Parse time-based properties that may be formatted as strings
    // Helper function to safely get time property values
    const getTimeValue = (value: unknown): string | number | null => {
      if (typeof value === 'string' || typeof value === 'number') {
        return value;
      }
      return null;
    };

    const parsedTrimStart = Element.parseTimeProperty(
      getTimeValue(data.trim_start) ?? getTimeValue(data.trimStart),
    );
    const parsedTrimDuration = Element.parseTimeProperty(
      getTimeValue(data.trim_duration) ?? getTimeValue(data.trimDuration),
    );
    const parsedFadeIn = Element.parseTimeProperty(
      getTimeValue(data.audio_fade_in),
    );
    const parsedFadeOut = Element.parseTimeProperty(
      getTimeValue(data.audio_fade_out),
    );

    // Convert string values to numbers or null
    const trimStart =
      parsedTrimStart !== null
        ? typeof parsedTrimStart === 'string'
          ? parseFloat(parsedTrimStart)
          : parsedTrimStart
        : null;

    const trimDuration =
      parsedTrimDuration !== null
        ? typeof parsedTrimDuration === 'string'
          ? parseFloat(parsedTrimDuration)
          : parsedTrimDuration
        : null;

    const fadeIn =
      parsedFadeIn !== null
        ? typeof parsedFadeIn === 'string'
          ? parseFloat(parsedFadeIn)
          : parsedFadeIn
        : null;

    const fadeOut =
      parsedFadeOut !== null
        ? typeof parsedFadeOut === 'string'
          ? parseFloat(parsedFadeOut)
          : parsedFadeOut
        : null;

    // Don't use Element.fromJSON directly as an object spread - instead extract proper properties
    // Parse time-based properties that Element would usually parse
    const parsedTime = Element.parseTimeProperty(
      getTimeValue(data.time) ??
        getTimeValue(data.start) ??
        getTimeValue(data.globalTime) ??
        0,
    );
    const parsedDuration = Element.parseTimeProperty(
      getTimeValue(data.duration),
    );

    // Ensure parsedTime is a number, default to 0 if null
    const timeValue = parsedTime !== null ? Number(parsedTime) : 0;

    return new AudioElement({
      // Base element properties
      id: typeof data.id === 'string' ? data.id : undefined,
      name: typeof data.name === 'string' ? data.name : null,
      track: typeof data.track === 'number' ? data.track : null,
      time: timeValue,
      duration: parsedDuration,
      type: 'audio', // Force audio type
      dynamic: typeof data.dynamic === 'boolean' ? data.dynamic : false,
      locked: typeof data.locked === 'boolean' ? data.locked : false,
      visible: typeof data.visible === 'boolean' ? data.visible : true,
      x: typeof data.x === 'string' ? data.x : '50%',
      y: typeof data.y === 'string' ? data.y : '50%',
      width: typeof data.width === 'string' ? data.width : '100%',
      height: typeof data.height === 'string' ? data.height : '100%',

      // Audio-specific properties
      source: audioSource,
      volume: volumeStr,
      trim_start: trimStart,
      trim_duration: trimDuration,
      loop: typeof data.loop === 'boolean' ? data.loop : false,
      audio_fade_in: fadeIn,
      audio_fade_out: fadeOut,
    });
  }
}
